import React, { useState, useEffect } from 'react';
import config from '../config';
import './CustomerPortal.css';

const CustomerPortal = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [downPayment, setDownPayment] = useState(0);
  const [emiAmount, setEmiAmount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${config.API_URL}/customer-portal/products`);
      const data = await response.json();
      
      setProducts(data);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(data.map(p => p.category))];
      setCategories(uniqueCategories);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const calculateEMI = (product) => {
    // Use the EMI info that's already calculated in the API response
    setDownPayment(product.emiInfo.downPayment);
    setEmiAmount(product.emiInfo.monthlyEMI);
    setSelectedProduct(product);
    setShowCalculator(true);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="customer-portal">
        <div className="loading">
          <div className="spinner"></div>
          <p>Products loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-portal">
      {/* Header */}
      <div className="portal-header">
        <h1>🛍️ A2Z Tak Store</h1>
        <p>Discover amazing products with easy EMI options</p>
        <div className="store-info">
          <p>📍 Punjab, India | 🌐 A2ztak.in</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="search-filter-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="category-filter">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      <div className="products-grid">
        {filteredProducts.map(product => (
          <div key={product._id} className="product-card">
            {product.imageUrl && (
              <div className="product-image">
                <img src={product.imageUrl} alt={product.name} />
              </div>
            )}
            
            <div className="product-info">
              <h3 className="product-name">{product.name}</h3>
              <p className="product-brand">{product.brand}</p>
              
              <div className="price-section">
                <div className="main-price">
                  ₹{product.currentPrice.toLocaleString()}
                </div>
                {product.offerPrice && product.offerPrice !== product.price && (
                  <div className="original-price">
                    ₹{product.price.toLocaleString()}
                  </div>
                )}
              </div>

              <div className="emi-info">
                <div className="downpayment">
                  <span className="label">Down Payment:</span>
                  <span className="amount">₹{product.emiInfo.downPayment.toLocaleString()}</span>
                </div>
                <div className="monthly-emi">
                  <span className="label">Monthly EMI:</span>
                  <span className="amount">₹{product.emiInfo.monthlyEMI.toLocaleString()}</span>
                </div>
                <div className="emi-duration">
                  <span className="label">Duration:</span>
                  <span className="duration">6 Months (No Cost EMI)</span>
                </div>
              </div>

              <button 
                className="calculate-emi-btn"
                onClick={() => calculateEMI(product)}
              >
                Calculate EMI
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* EMI Calculator Modal */}
      {showCalculator && selectedProduct && (
        <div className="emi-calculator-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>EMI Calculator</h2>
              <button 
                className="close-btn"
                onClick={() => setShowCalculator(false)}
              >
                ×
              </button>
            </div>
            
            <div className="product-details">
              <h3>{selectedProduct.name}</h3>
              <p className="brand">{selectedProduct.brand}</p>
              <div className="price">₹{selectedProduct.currentPrice.toLocaleString()}</div>
            </div>

            <div className="emi-breakdown">
              <div className="breakdown-item">
                <span className="label">Product Price:</span>
                <span className="value">₹{selectedProduct.currentPrice.toLocaleString()}</span>
              </div>
              
              <div className="breakdown-item">
                <span className="label">Down Payment (40%):</span>
                <span className="value">₹{downPayment.toLocaleString()}</span>
              </div>
              
              <div className="breakdown-item">
                <span className="label">Loan Amount:</span>
                <span className="value">₹{(selectedProduct.currentPrice - downPayment).toLocaleString()}</span>
              </div>
              
              <div className="breakdown-item">
                <span className="label">Monthly EMI (6 months):</span>
                <span className="value emi-highlight">₹{emiAmount.toLocaleString()}</span>
              </div>
              
              <div className="breakdown-item">
                <span className="label">Interest Rate:</span>
                <span className="value">0% (No Cost EMI)</span>
              </div>
            </div>

            <div className="emi-schedule">
              <h4>Payment Schedule</h4>
              <div className="schedule-table">
                <div className="schedule-header">
                  <span>Month</span>
                  <span>EMI Amount</span>
                  <span>Status</span>
                </div>
                {[1, 2, 3, 4, 5, 6].map(month => (
                  <div key={month} className="schedule-row">
                    <span>Month {month}</span>
                    <span>₹{emiAmount.toLocaleString()}</span>
                    <span className="status">Pending</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="contact-info">
              <p>📞 Contact us for more information and to apply for EMI</p>
              <p>📍 Visit our store for instant approval</p>
              <p>💳 Payment Options: Cash, Credit Card, UPI, Self Finance</p>
            </div>
          </div>
        </div>
      )}

      {filteredProducts.length === 0 && (
        <div className="no-products">
          <p>No products found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CustomerPortal;
